import * as Yup from "yup";
import { IOption } from "../../data/AppInterface";
import { TBitValue, TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  ASSIGN_SUBSCRIPTION,
  GET_ASSIGNABLE_SUBSCRIPTION_PACKAGE,
  GET_FIRM_SUBSCRIPTION_DETAIL,
  GET_SUBSCRIPTION_ASSIGNMENT_PREVIEW,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";
import { getPlanTypeLabel } from "../../utils/PurchaseConstants";
import { formatCredits } from "../../utils/PurchaseFormat";
import {
  ASSIGN_RESULT_ENUM,
  CREDIT_TYPE_ENUM,
  SUBSCRIPTION_SOURCE_ENUM,
  VISIBILITY_TYPE_ENUM,
} from "../../utils/PurchaseEnumerations";

export interface IAssignableSubscriptionPackage {
  id: string;
  name: string;
  validity: number;
  visibility_type: VISIBILITY_TYPE_ENUM;
  is_active: TBitValue;
  base_price: string;
  cgst: string;
  sgst: string;
  igst: string;
  total: string;
  platforms: string[];
}

interface IFirmLatestSubscription {
  id: string;
  subscription_package_name: string;
  source: SUBSCRIPTION_SOURCE_ENUM;
  start_date: string;
  end_date: string;
  remaining_days: number;
  is_expired: TBitValue;
}

export interface IFirmSubscriptionDetail {
  firm_id: string;
  firm_name: string;
  subscription: IFirmLatestSubscription | null;
}

interface IAssignmentCreditPreview {
  credit_type: CREDIT_TYPE_ENUM;
  balance_before: number | null;
  balance_after: number | null;
  is_provided: boolean;
  included_value: number | null;
  over_cap: boolean;
}

interface IAssignmentReplacedPackage {
  package_name: string;
  remaining_days: number;
  message: string;
}

export interface IAssignmentPreview {
  package_name: string;
  validity: number;
  credits: IAssignmentCreditPreview[];
  over_cap_message: string | null;
  replaces: IAssignmentReplacedPackage | null;
}

export interface IFirmSubscriptionAssignmentForm {
  firm_id: string;
  subscription_package_id: string;
}


export const FIRM_SUBSCRIPTION_ASSIGNMENT_INITIAL_VALUES: IFirmSubscriptionAssignmentForm = {
  firm_id: "",
  subscription_package_id: "",
};

const CONTACT_TO_SALES_VALUE = -1;

export const firmSubscriptionAssignmentValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    firm_id: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [["fieldName", t("firm_name")]]),
    ),
    subscription_package_id: Yup.string().required(
      prepareMessageFromParams(t("error_message_required"), [
        ["fieldName", t("subscription_package")],
      ]),
    ),
  });

export const getPackageOptions = (
  t: TUseTranslationTfn,
  packages: IAssignableSubscriptionPackage[],
): IOption<string>[] =>
  packages.map((item) => ({
    data_value: item.id,
    display_value: `${item.name} (${getPlanTypeLabel(t, item.platforms)})${
      item.is_active === BIT_VALUE.One ? "" : ` - ${t("inactive")}`
    }`,
  }));

export const getIncludedCreditLabel = (
  t: TUseTranslationTfn,
  credit: IAssignmentCreditPreview,
): string => {
  if (!credit.is_provided) return t("not_provided");
  if (credit.included_value === null) return t("unlimited");
  if (credit.included_value === CONTACT_TO_SALES_VALUE) return t("contact_to_sales");
  return `+${formatCredits(t, credit.included_value)}`;
};

export const fetchAssignableSubscriptionPackage = async (
  setPackages: TReactSetState<IAssignableSubscriptionPackage[]>,
) => {
  try {
    const response = await GET_ASSIGNABLE_SUBSCRIPTION_PACKAGE();
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setPackages(response.data ?? []);
      return;
    }
    setPackages([]);
    toastError(response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
  } catch (error) {
    handleError(error, "fetchAssignableSubscriptionPackage");
  }
};

export const fetchFirmSubscriptionDetail = async (
  firmId: string,
): Promise<IFirmSubscriptionDetail | undefined> => {
  try {
    const response = await GET_FIRM_SUBSCRIPTION_DETAIL(firmId);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      return response.data;
    }
    toastError(response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
  } catch (error) {
    handleError(error, "fetchFirmSubscriptionDetail");
  }
  return undefined;
};

export const fetchAssignmentPreview = async (
  firmId: string,
  subscriptionPackageId: string,
): Promise<IAssignmentPreview | undefined> => {
  try {
    const response = await GET_SUBSCRIPTION_ASSIGNMENT_PREVIEW(firmId, subscriptionPackageId);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      return response.data;
    }
    toastError(response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
  } catch (error) {
    handleError(error, "fetchAssignmentPreview");
  }
  return undefined;
};

export const assignSubscription = async (
  values: IFirmSubscriptionAssignmentForm,
  confirmReplace: boolean,
  setLoading: TReactSetState<boolean>,
): Promise<ASSIGN_RESULT_ENUM> => {
  setLoading(true);
  try {
    const response = await ASSIGN_SUBSCRIPTION({ ...values, confirm_replace: confirmReplace });
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: response.message });
      return ASSIGN_RESULT_ENUM.Assigned;
    }
    if (response?.data?.data?.requires_confirmation === true) {
      return ASSIGN_RESULT_ENUM.NeedsConfirmation;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "assignSubscription");
  } finally {
    setLoading(false);
  }
  return ASSIGN_RESULT_ENUM.Failed;
};
